import { AxisOptions, AxisTickPositionerCallbackFunction, Options } from 'highcharts/highstock';
import { cloneDeep, merge } from 'lodash';

import { arrayMinMax } from '../services/util';

import { getColorForEventType, getOppositeTheme, IColorTheme } from './color-themes';
import { LimitTypes } from './dataset';
import { IEvent, IEventType } from './events-data';
import { SeriesDisplayMode } from './menu-options';
import { ILimits, ITypeConversions } from './metadata';

/** ensures there is a tick positioner for each integer between a min and a max */
export const tickPositionerEveryInteger: AxisTickPositionerCallbackFunction = function() {
    let min = Math.floor( ( this as any ).dataMin );
    const max = Math.ceil( ( this as any ).dataMax );
    const ticks: Array<number> = [];
    while ( min <= max ) {
        ticks.push(min);
        min++;
    }
    return ticks;
};

export const newAxisConfig: AxisOptions = {
    crosshair: false,
    opposite: true,
    showLastLabel: true,
    tickPixelInterval: 45,
    lineWidth: 1,
    labels: {
        style: {
            textOverflow: 'none' // disable ellipsis (WEBTCAD-1201)
        }
    }
};

export const DEFAULT_HIGHSTOCK_CONFIG: Options = {
    annotations: [],
    chart: {
        animation: false,
        backgroundColor: 'rgba(255, 255, 255, 0.0)',
        resetZoomButton: {
            theme: {
                display: 'none'
            }
        },
        zoomType: 'x',
        panning: false,
        spacingBottom: 8,
        spacingLeft: 16,
        spacingRight: 16
    },
    credits: {
        enabled: false // we hide the credits here, but ensure that they are visible somewhere on the page
    },
    exporting: {
        enabled: false, // hide the default highcharts exporting menu
        fallbackToExportServer: false,
        chartOptions: {
            legend: {
                labelFormat: '{name}'
            }
        }
    },
    legend: {
        alignColumns: false,
        enabled: true,
        labelFormatter: undefined,
        title: { text: ' ' },
        itemStyle: { textOverflow: 'null' },
        padding: 0,
        margin: 0,
        align: 'left',
        verticalAlign: 'top'
    },
    navigator: {
        adaptToUpdatedData: false,
        xAxis: {
            labels: {
                formatter: undefined
            }
        },
        enabled: true
    },
    plotOptions: {
        line: {
            dataGrouping: {
                groupPixelWidth: 1,
                enabled: true
            },
            marker: {
                radius: 2,
                enabled: undefined
            },
            lineWidth: 1.2,
            step: undefined
        },
        arearange: {
            boostThreshold: 2000,
            fillOpacity: 0.35,
            lineWidth: 0,
            visible: true
        },
        polygon: {
            // polygon is used for events
            cursor: 'pointer',
            linecap: 'square',
            showInLegend: false
        },
        series: {
            connectNulls: false,
            softThreshold: false,
            states: {
                inactive: {
                    opacity: 1
                },
                hover: {
                    enabled: false
                }
            },
            events: {
                click: undefined
            }
        }
    },
    rangeSelector: {
        enabled: false,
        inputEnabled: false
    },
    scrollbar: {
        liveRedraw: false,
        enabled: true
    },
    title: {
        text: ''
    },
    xAxis: [ {
        type: 'datetime',
        title: {},
        labels: {
            staggerLines: 1,
            step: 1,
            formatter: undefined
        },
        events: {
            afterSetExtremes: undefined,
            setExtremes: undefined
        },
        minRange: Number.MIN_VALUE,
        offset: 0,
        ordinal: false,
        plotLines: [],
        plotBands: []
    } ],
    yAxis: [ {
        min: null,
        max: null,
        startOnTick: false,
        endOnTick: false,
        crosshair: false,
        breaks: [],
        opposite: false,
        plotBands: [],
        tickPixelInterval: 45,
        lineWidth: 1,
        labels: {
            align: 'right',
            x: -5,
            y: 4,
            formatter: undefined,
            style: {
                textOverflow: 'none', // disable ellipsis (WEBTCAD-1201)
                whiteSpace: 'nowrap'
            }
        },
        type: 'linear'
    }, {
        // second y-axis, used for events overlay
        className: 'events-y-axis',
        // shade this area to distinguish it from the rest of the plot
        plotBands: [ {
            className: 'events-plot-band',
            from: -Infinity,
            to: Infinity,
            zIndex: 0
        } ],
        offset: 0,
        top: '100%',
        opposite: false,
        // Put lower y-values on the top and higher values on the bottom.
        // This causes the order of labels on the axis to match the order in
        // the View->EventTypes menu (which is also sorted numerically ascending by event type)
        reversed: true,
        visible: false,
        labels: {
            align: 'right',
            x: -5,
            y: 13,
            style: {
                textOverflow: 'none'
            }
        },
        tickInterval: 1,
        type: 'category',
        tickPositions: []
    } ]
};

export function setSeriesClickHandler( config: Options ): Options {
    config.plotOptions.series.events.click = function() {
        if ( this.options.type === 'polygon' ) {
            console.log( this.options['eventDetails'] );
        } else {
            window.open( this.options['url'], '_self' );
        }
    };
    return config;
}

export function setRangeChangeHandler( config: Options, cb: Function ): Options {
    config.xAxis[0].events.afterSetExtremes = cb;
    return config;
}

export function setYAxisLabels( config: Options ): Options {
    config.yAxis[0].labels.formatter = undefined;
    return config;
}

export function setConnectNulls( config: Options, enabled: boolean ) {
    config.plotOptions.series.connectNulls = enabled;
    return config;
}

export function setRangeVisibility( config: Options, enabled: boolean ) {
    config.plotOptions.arearange.visible = enabled;
    return config;
}

/**
 * Sets the values of the yellow and red limit areas and draws bands representing the limit areas behind the series on the chart.
 */
export function setPlotBands( config: Options, limits: ILimits, colorTheme: IColorTheme ) {
    const maxVal = 1e300;
    config.yAxis[0].plotBands = [ {
        // Yellow low band
        color: colorTheme.limits.bands.warn,
        from: limits.red.low,
        to: limits.yellow.low,
        limitType: LimitTypes.WARN,
        zIndex: 1
    }, {
        // Yellow high band
        color: colorTheme.limits.bands.warn,
        from: limits.yellow.high,
        to: limits.red.high,
        limitType: LimitTypes.WARN,
        zIndex: 1
    }, {
        // Red low band
        color: colorTheme.limits.bands.bad,
        from: -maxVal,
        to: limits.red.low,
        limitType: LimitTypes.BAD,
        zIndex: 1
    }, {
        // Red high band
        color: colorTheme.limits.bands.bad,
        from: limits.red.high,
        to: maxVal,
        limitType: LimitTypes.BAD,
        zIndex: 1
    } ];
    return config;
}

/**
 * Hides the red and yellow limit areas.
 */
export function disableYAxisLimitBands( config: Options ): Options {
    // Logger.log( 'Disabling y-axis limit bands' );
    config.yAxis[0].plotBands = [];
    return config;
}

/**
 * Set the min/max value and padding for the y axis visible range
 * @param paddingPercent A number ranging from 0 - 100 specifying how much of the range to add as padding
 */
export function setYAxisRange( config: Options, low: number, high: number, paddingPercent = 0 ): Options {
    // Logger.log( 'Setting y-axis scaling:', low, high, paddingPercent );
    const padding = paddingPercent * ( high - low );
    config.yAxis[0].min = (low === null) ? null : low - padding;
    config.yAxis[0].max = (high === null) ? null : high + padding;
    return config;
}

/**
 * For charts with discrete data, defines how y-axis values are formatted.
 */
export function setDiscreteFormatters( config: Options, conversions: ITypeConversions[]  ): Options {
    if ( !conversions ) {
        config.yAxis[0].labels.formatter = undefined;
    } else {
        config.yAxis[0].labels.formatter = function() {
            const self = this;
            const typeConversion = conversions.find( function( conversion ) { return conversion.value === self.value; });
            return typeConversion ? typeConversion.state : undefined;
        };
    }
    // force ticks to show at every integer
    config.yAxis[0].tickPositioner = tickPositionerEveryInteger;
    config.plotOptions.line.step = 'left';

    return config;
}

/**
 * For charts with discrete data, hides all labels on the y-axis except for those associated with y-values of the loaded data.
 */
export function hideUnusedDiscreteLabels( config: Options, breaks, usedDiscreteVals ): Options {
    // breaks should be an array of objects, like:
    // [{from: 1, to: 3}, {from: 5, to: 14}]
    const minmax = arrayMinMax( usedDiscreteVals );
    // normally the padding percent is 0.1, but we need to set it based on the number of visible values
    // not based on the range of min to max. See WEBTCAD-1201
    // If only one discrete value is used, the padding ends up being Infinity. Don't let this happen
    const alteredPadding = ( usedDiscreteVals.length === 1 ) ? 0 : 0.05 * usedDiscreteVals.length / (minmax.max - minmax.min );
    config = setYAxisRange( config, minmax.min, minmax.max, alteredPadding );
    config.yAxis[0].breaks = breaks;
    return config;
}

/**
 * For charts with discrete data, shows all values on the y-axis.
 */
export function showAllDiscreteLabels( config: Options, conversions: any[] ): Options {
    // find min and max numeric values of the labels for all the datasets
    let allConversions = [];
    conversions.forEach( function(conversion) {
        allConversions = allConversions.concat( conversion );
    });
    const minmax = arrayMinMax( allConversions, function(el) {
        return el.value;
    });
    config = setYAxisRange( config, minmax.min, minmax.max, 0.05 );
    config.yAxis[0].breaks = undefined;
    return config;
}

 /**
  * Adds spacecraft events to the chart, or disables them.
  * @param enabled Whether to show spacecraft events.
  * @param [eventsData] The spacecraft events data (a list of events, and a list of possible event types for the mission).
  *                     Required if `enabled` is `true`.
  * @param [viewEventTypes] A whitelist of event type IDs to show. Required if `enabled` is `true`.
  */
export function setEventsOverlay( initialConfig: Options,
                                  enabled: boolean,
                                  events: IEvent[],
                                  types: IEventType[],
                                  viewEventTypes: number[],
                                  colorTheme: IColorTheme ): Options {
    // The index values of the list of event types equates to y-values on the axis.
    // i.e. events of the first type are drawn between the y-values of 0 and 1.
    // viewEventTypes is an array of strings, each one a type of event which is allowed to show.
    const config = cloneDeep( initialConfig );
    // reset plot bands and possibly add them in later
    config.xAxis[0].plotBands = [];
    config.xAxis[0].plotLines = [];

    if ( enabled && viewEventTypes.length !== 0 ) {
        // make a copy of the original viewEventTypes array so we can safely modify it
        viewEventTypes = cloneDeep( viewEventTypes );
        // there are some events that the user may want to hide... we also want to hide rows on the y-axis
        // for which there are no events in the shown time range. Look through all the events to see
        // which types are present, and hide the rest.
        const presentEventTypeIds = [];
        events.forEach( event => {
            if ( !presentEventTypeIds.includes( event.type.id ) ) {
                presentEventTypeIds.push( event.type.id );
            }
        });
        viewEventTypes = viewEventTypes.filter( typeId => presentEventTypeIds.includes( typeId ) );
        const numRows = viewEventTypes.length;

        const tickPositions = [];
        viewEventTypes.forEach( viewEventType => {
            tickPositions.push( types.findIndex( type => viewEventType === type.id ) );
        });
        config.yAxis[1].tickPositions = tickPositions;

        // set y-axis breaks for events
        let min = Number.MAX_VALUE;
        let max = -Number.MAX_VALUE;
        config.yAxis[1].breaks = [];
        // first find the min and max values of the axis
        types.forEach( (type, i) => {
            if ( viewEventTypes.includes( type.id ) ) {
                min = Math.min( min, i );
                max = Math.max( max, i );
            }
        });
        max++;

        // now add axis breaks
        types.forEach( (type, i) => {
            if ( viewEventTypes.indexOf(type.id) === -1 && i < max && i >= min ) {
                config.yAxis[1].breaks.push({
                    // constructing the break like this ensures that the correct labels will be shown.
                    // Putting breaks on the exact values that labels live on can hide labels in some unpredictable ways.
                    from: i - 0.00001,
                    to: i + 0.99999
                });
            }
        });

        config.yAxis[1].min = min;
        config.yAxis[1].max = max;

        // make room for the event area and show it
        // also set the max and height for the event y-axis
        const eventRowHeight = 16; // an arbitrary value. 16 makes for a nice clickable thickness without taking too much space.
        config.yAxis[1].height = config.xAxis[0].offset = eventRowHeight * numRows;
        config.yAxis[1].visible = true;

        config.yAxis[1].labels.formatter = function() {
            const eventType = types[ this.value ];
            if ( eventType == null ) {
                return '';
            }
            // When the series uses the 'light' colors, the labels use the 'dark', colors, and vice versa
            const color = getColorForEventType( eventType, types, getOppositeTheme( colorTheme.name ) ).series;
            return '<div class="events-label" title="' + eventType.label + '" style="color:' + color + '">' + eventType.label + '</div>';
        };

        // set x-axis bands and lines
        // for each event with a start and end, create a band. If the event only has a start, create a line.'
        events.filter( event => viewEventTypes.includes( event.type.id ) ).forEach( event => {
            const eventTypeColor = getColorForEventType( event.type, types, colorTheme );
            if ( event.start === event.end ) {
                config.xAxis[0].plotLines.push({
                    color: eventTypeColor.line,
                    value: event.start,
                    width: 2,
                    zIndex: 1
                });
            } else {
                config.xAxis[0].plotBands.push({
                    color: eventTypeColor.band,
                    from: event.start,
                    to: event.end,
                    zIndex: 1
                });
            }
        });
    } else {
        config.yAxis[1].height = config.xAxis[0].offset = 0;
        config.yAxis[1].visible = false;
    }

    return config;
}

/**
 * Defines which color theme to use for the chart.
 */
export function setColorTheme( conf: Options, colorTheme: IColorTheme ): Options {
    // apply the theme to the highcharts config
    const config = cloneDeep( conf );
    merge( config, {
        chart: {
            selectionMarkerFill: colorTheme.selectionMarkerFill
        },
        colors: colorTheme.colors,
        legend: {
            title: {
                style: colorTheme.legend.titleStyle
            },
            itemStyle: colorTheme.legend.itemStyle,
            itemHoverStyle: colorTheme.legend.itemStyle
        },
        navigator: {
            outlineColor: colorTheme.navigator.outlineColor,
            maskFill: colorTheme.navigator.maskFill,
            handles: {
                backgroundColor: colorTheme.navigator.handles.backgroundColor,
                borderColor: colorTheme.navigator.handles.borderColor
            },
            xAxis: {
                labels: {
                    style: colorTheme.axis.labelStyle
                },
                gridLineColor: colorTheme.axis.gridLineColor
            }
        }
    });

    merge( config.xAxis[0], {
        gridLineColor: colorTheme.axis.gridLineColor,
        lineColor: colorTheme.axis.lineColor,
        minorGridLineColor: colorTheme.axis.minorGridLineColor,
        minorTickColor: colorTheme.axis.minorTickColor,
        tickColor: colorTheme.axis.tickColor,
        crosshair: {
            color: colorTheme.axis.crosshairColor
        },
        labels: {
            style: colorTheme.axis.labelStyle
        }
    });

    merge( config.yAxis[0], {
        gridLineColor: colorTheme.axis.gridLineColor,
        lineColor: colorTheme.axis.lineColor,
        minorGridLineColor: colorTheme.axis.minorGridLineColor,
        minorTickColor: colorTheme.axis.minorTickColor,
        tickColor: colorTheme.axis.tickColor,
        crosshair: config.yAxis[0].crosshair ? { color: colorTheme.axis.crosshairColor } : false
    });

    // set the colors of the limit bands/lines
    if ( config.yAxis[0].plotBands ) {
        for ( let i = 0; i < config.yAxis[0].plotBands.length; i++ ) {
            config.yAxis[0].plotBands[i].color = ( config.yAxis[0].plotBands[i].limitType === LimitTypes.WARN ) ?
            colorTheme.limits.bands.warn :
            colorTheme.limits.bands.bad;
        }
    }

    return config;
}

/** Sets whether a series is displayed with points, lines or both */
export function setSeriesDisplayMode( config: Options, displayMode: SeriesDisplayMode ): Options {
    config.plotOptions.line.marker.enabled = displayMode === 'lines' ? undefined : true;
    config.plotOptions.line.lineWidth = displayMode === 'points' ? 0 : 1.2;
    return config;
}
